import { X } from "lucide-react";

import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { Button, Modal, ModalBody, ModalHeader } from "@design-lab/shared/ui";

type ConfirmActionModalProps = {
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  loadingText?: string;
  cancelText?: string;
  isLoading?: boolean;
};

export function ConfirmActionModal({
  isOpen,
  onConfirm,
  onClose,
  title = "Turn Off Background Removal?",
  message = "Disabling this will undo all background edits you've made. Do you want to continue?",
  confirmText = "Turn Off",
  loadingText = "Processing...",
  cancelText = "Keep Changes",
  isLoading = false
}: ConfirmActionModalProps) {
  const { isDesktop } = useResponsive();

  const modalClassName = isDesktop
    ? "w-[420px] max-w-[90vw] h-auto"
    : "absolute bottom-0 left-0 right-0 flex h-auto max-h-[90vh] w-full flex-col rounded-b-none rounded-t-2xl";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={modalClassName}
      backdropClassName="fixed z-50"
      closeOnBackdropClick={false}
      closeOnEsc={false}
      title={title}
      description={message}
    >
      <ModalHeader className="justify-start border-0 p-4 pb-0 text-base font-medium text-gray-900">
        {title}
        <button
          type="button"
          onClick={onClose}
          className="focus-visible:ring-ring absolute right-4 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          data-testid="modal-close-button"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>
      </ModalHeader>
      <ModalBody className="space-y-4">
        <p className="text-base text-gray-700">{message}</p>

        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            onPress={onConfirm}
            className="w-full border-blue-600 px-4 py-2.5 text-blue-600 hover:border-blue-700 hover:bg-blue-50 hover:text-blue-700"
            isDisabled={isLoading}
          >
            {isLoading ? loadingText : confirmText}
          </Button>
          <Button
            onPress={onClose}
            className="w-full bg-blue-600 px-4 py-2.5 text-white hover:bg-blue-700"
            isDisabled={isLoading}
          >
            {cancelText}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}
